import type { BakedBlockInstance, BlockDefinition, Zone } from '@tsagko/label-contracts'
import { mergeVariables } from '../block-configurator/variables-ops'
import { bakeBlock, type PropValues } from './bake-block'

export function rebakeWithVersion(
  currentBaked: BakedBlockInstance,
  sourceBlock: BlockDefinition,
  _hostBox: { widthMm: number; heightMm: number }
): BakedBlockInstance {
  const stringValues: Record<string, string> = {}
  for (const [k, v] of Object.entries(currentBaked.props)) {
    stringValues[k] = typeof v === 'string' ? v : v != null ? String(v) : ''
  }
  return bakeBlock(sourceBlock, stringValues, undefined, () => currentBaked.id)
}

export function rebakeWithLatest(
  baked: BakedBlockInstance,
  latest: BlockDefinition,
  now: () => string = () => new Date().toISOString(),
  idGen: () => string = () => baked.id
): BakedBlockInstance {
  const values: PropValues = {}
  for (const [k, v] of Object.entries(baked.props)) {
    values[k] = typeof v === 'string' ? v : v != null ? String(v) : ''
  }
  const next = bakeBlock(latest, values, now, idGen)
  return { ...next, id: baked.id }
}

export function clearBakedInstanceAt(rootZone: Zone, path: string[]): Zone {
  if (path.length === 0) {
    const { component: _component, ...rest } = rootZone
    return rest
  }
  if (!rootZone.children || !rootZone.split) {
    throw new Error(
      `clearBakedInstanceAt: cannot descend into leaf at path ${path.join('/')}`,
    )
  }
  const head = path[0]
  const index = Number(head)
  if (index !== 0 && index !== 1) {
    throw new Error(`clearBakedInstanceAt: invalid path segment "${head}"`)
  }
  const childIndex = index as 0 | 1
  const child = rootZone.children[childIndex]
  const updatedChild = clearBakedInstanceAt(child, path.slice(1))
  const nextChildren: [Zone, Zone] =
    childIndex === 0
      ? [updatedChild, rootZone.children[1]]
      : [rootZone.children[0], updatedChild]
  return { ...rootZone, children: nextChildren }
}

export interface StalenessReport {
  current: { blockId: string; blockVersion: number } | undefined
  target: { blockId: string; blockVersion: number } | undefined
  /** Variable names in the target's canonical set but not the current's. */
  addedVariables: string[]
  /** Variable names in the current's canonical set but not the target's. */
  removedVariables: string[]
}

/**
 * Diff the CANONICAL variable sets of two block-definition versions
 * (def-vs-def). The canonical set per side = `mergeVariables(layout, variables)`
 * — layout `{var}` references overlaid with persisted metadata, because
 * `def.variables` alone is a SPARSE metadata map (pruneOrphans drops bare
 * layout refs like `{lot}` on save).
 *
 * Deliberately NOT instance-props-based: `BakedBlockInstance.props` carries
 * only explicitly-SET values (defaulted variables are absent), so diffing a
 * target def against props would report false "+new" entries for variables
 * that exist unchanged in both versions.
 *
 * Either side `undefined` → empty diff (nothing to compare against).
 */
export function buildStalenessReport(
  currentDef: BlockDefinition | undefined,
  targetDef: BlockDefinition | undefined
): StalenessReport {
  let added: string[] = []
  let removed: string[] = []
  if (currentDef && targetDef) {
    const currentSet = mergeVariables(currentDef.layout, currentDef.variables ?? {})
    const targetSet = mergeVariables(targetDef.layout, targetDef.variables ?? {})
    added = Object.keys(targetSet).filter(n => !(n in currentSet))
    removed = Object.keys(currentSet).filter(n => !(n in targetSet))
  }
  return {
    current: currentDef
      ? { blockId: currentDef.id, blockVersion: currentDef.version }
      : undefined,
    target: targetDef
      ? { blockId: targetDef.id, blockVersion: targetDef.version }
      : undefined,
    addedVariables: added,
    removedVariables: removed,
  }
}

/**
 * Coerce a `BakedBlockInstance.props` map (typed `Record<string, unknown>`)
 * into the all-string shape `bakeBlock` expects. Mirrors the inline coercion
 * already used by `rebakeWithVersion`/`rebakeWithLatest`.
 */
export function propsToStrings(props: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [k, v] of Object.entries(props)) {
    out[k] = typeof v === 'string' ? v : v != null ? String(v) : ''
  }
  return out
}

/**
 * Re-bake an instance against its source block with a fresh `values` map,
 * preserving the instance id and placement. The caller loads the SAME version
 * the instance was baked at, so `blockVersion` is preserved too. Used by the
 * inline template-variables form to commit a single edited field.
 */
export function rebakeWithValues(
  baked: BakedBlockInstance,
  sourceBlock: BlockDefinition,
  values: PropValues,
): BakedBlockInstance {
  const next = bakeBlock(sourceBlock, values, undefined, () => baked.id)
  return { ...next, placement: baked.placement }
}

export interface BakedInstanceEntry {
  path: string[]
  zoneId: string
  baked: BakedBlockInstance
}

/**
 * Walk a zone tree and return every baked-block-instance with its canvas path
 * (`'0'`/`'1'` segments) and the id of the zone that hosts it. Mirrors the
 * recursion shape of `applyVariablesToLayout`/`findBakedPath`.
 */
export function collectBakedInstances(zone: Zone, acc: string[] = []): BakedInstanceEntry[] {
  if (zone.component?.type === 'baked-block-instance') {
    return [{ path: acc, zoneId: zone.id, baked: zone.component }]
  }
  if (zone.children) {
    return [
      ...collectBakedInstances(zone.children[0], [...acc, '0']),
      ...collectBakedInstances(zone.children[1], [...acc, '1']),
    ]
  }
  // Empty leaf zone (no component, no children) — legitimate in templates;
  // correctly yields nothing. Do NOT add a throwing guard like clearBakedInstanceAt.
  return []
}
